import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "CobrosSuscripciones";

const normalizarTexto = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const convertirAMayuscula = (valor = "") =>
  String(valor).toLocaleUpperCase("es-CO");

const normalizarMayuscula = (valor = "") =>
  convertirAMayuscula(valor)
    .replace(/\s+/g, " ")
    .trim();

const leerSesionLocal = () => {
  try {
    const permisosStorage = localStorage.getItem("permisos");
    const usuarioStorage = localStorage.getItem("usuario");
    const userStorage = localStorage.getItem("user");
    const usuariosStorage = localStorage.getItem("usuarios");

    return permisosStorage
      ? JSON.parse(permisosStorage)
      : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
          ? JSON.parse(userStorage)
          : usuariosStorage
            ? JSON.parse(usuariosStorage)
            : {};
  } catch (error) {
    console.error("Error leyendo información local:", error);
    return {};
  }
};

const obtenerModulo = (nombreModulo) => {
  try {
    const data = leerSesionLocal();

    const modulos = Array.isArray(data)
      ? data
      : data?.modulos ||
        data?.permisos ||
        data?.usuario?.modulos ||
        data?.usuario?.permisos ||
        data?.user?.modulos ||
        data?.user?.permisos ||
        [];

    const buscado = normalizarTexto(nombreModulo);

    return (
      modulos.find((modulo) => {
        const nombre =
          modulo?.nombreModulo ||
          modulo?.modulo ||
          modulo?.nombre ||
          modulo?.moduloObjectId?.nombreModulo ||
          modulo?.moduloObjectId?.nombre ||
          "";

        return normalizarTexto(nombre) === buscado;
      }) || {}
    );
  } catch (error) {
    console.error("Error leyendo permisos:", error);
    return {};
  }
};

const obtenerRolNombre = () => {
  const data = leerSesionLocal();

  const rol =
    data?.rolObjectId?.nombreRol ||
    data?.rol?.nombreRol ||
    data?.rol?.nombre ||
    data?.rol ||
    data?.role ||
    data?.nombreRol ||
    data?.usuario?.rolObjectId?.nombreRol ||
    data?.usuario?.rol?.nombreRol ||
    data?.usuario?.rol?.nombre ||
    data?.usuario?.rol ||
    data?.user?.rolObjectId?.nombreRol ||
    data?.user?.rol?.nombreRol ||
    data?.user?.rol?.nombre ||
    data?.user?.rol ||
    "";

  return String(rol).trim().toUpperCase();
};

const parsearListaDias = (valor = "") => {
  const dias = String(valor)
    .split(",")
    .map((item) => Number(String(item).trim()))
    .filter((numero) => Number.isFinite(numero) && numero >= 0);

  return Array.from(new Set(dias)).sort((a, b) => b - a);
};


const RegistrarPlanes = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const edit = Boolean(id);

  const [codigoPlan, setCodigoPlan] = useState("");
  const [nombrePlan, setNombrePlan] = useState("");
  const [descripcionPlan, setDescripcionPlan] = useState("");
  const [tipoDuracion, setTipoDuracion] = useState("MESES");
  const [cantidadDuracion, setCantidadDuracion] = useState("1");
  const [valorPlan, setValorPlan] = useState("");
  const [moneda, setMoneda] = useState("COP");
  const [diasGracia, setDiasGracia] = useState("0");
  const [diasAvisoTexto, setDiasAvisoTexto] = useState("7, 3, 1");
  const [permiteRenovacion, setPermiteRenovacion] = useState(true);
  const [tipoPlan, setTipoPlan] = useState("COMERCIAL");
  const [estadoPlan, setEstadoPlan] = useState(true);
  const [ordenVisualizacion, setOrdenVisualizacion] = useState("0");

  const [loading, setLoading] = useState(false);

  const moduloCobros = useMemo(() => obtenerModulo(MODULO), []);

  const puedeCrear =
    moduloCobros?.puedeCrear === true ||
    moduloCobros?.crear === true ||
    moduloCobros?.create === true;

  const puedeLeer =
    moduloCobros?.puedeLeer === true ||
    moduloCobros?.leer === true ||
    moduloCobros?.read === true;

  const puedeActualizar =
    moduloCobros?.puedeActualizar === true ||
    moduloCobros?.actualizar === true ||
    moduloCobros?.editar === true ||
    moduloCobros?.update === true;

  const rolNombre = useMemo(() => obtenerRolNombre(), []);
  const esInstructor = rolNombre === "INSTRUCTOR";
  const tienePermisoFormulario = edit ? puedeActualizar : puedeCrear;

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const manejarErrorSesion = (error) => {
    if (error?.response?.status === 401) {
      Swal.fire({
        icon: "warning",
        title: "Sesión expirada",
        text: "Por favor inicia sesión nuevamente.",
        confirmButtonText: "Aceptar",
      }).then(() => {
        localStorage.removeItem("token");
        navigate("/", { replace: true });
      });

      return true;
    }

    return false;
  };


  const cleanData = () => {
    setCodigoPlan("");
    setNombrePlan("");
    setDescripcionPlan("");
    setTipoDuracion("MESES");
    setCantidadDuracion("1");
    setValorPlan("");
    setMoneda("COP");
    setDiasGracia("0");
    setDiasAvisoTexto("7, 3, 1");
    setPermiteRenovacion(true);
    setTipoPlan("COMERCIAL");
    setEstadoPlan(true);
    setOrdenVisualizacion("0");
  };

  const handleCancelOrClean = () => {
    if (edit) {
      navigate("/cobros/planes");
    } else {
      cleanData();
    }
  };

  useEffect(() => {
    const cargarPlan = async () => {
      if (!id) return;

      if (!puedeLeer && !puedeActualizar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para consultar planes.",
          "warning"
        );
        return;
      }

      try {
        setLoading(true);

        const { data } = await axios.get(
          `/api/planes/listid/${id}`,
          authConfig()
        );

        const plan = data?.message;

        if (!data?.ok || !plan) {
          throw new Error(data?.message || "No se pudo cargar el plan.");
        }

        setCodigoPlan(plan.codigoPlan || "");
        setNombrePlan(plan.nombrePlan || "");
        setDescripcionPlan(plan.descripcionPlan || "");
        setTipoDuracion(plan.tipoDuracion || "MESES");
        setCantidadDuracion(String(plan.cantidadDuracion ?? 1));
        setValorPlan(String(plan.valorPlan ?? ""));
        setMoneda(plan.moneda || "COP");
        setDiasGracia(String(plan.diasGracia ?? 0));
        setDiasAvisoTexto(
          Array.isArray(plan.diasAvisoVencimiento)
            ? plan.diasAvisoVencimiento.join(", ")
            : "7, 3, 1"
        );
        setPermiteRenovacion(plan.permiteRenovacion !== false);
        setTipoPlan(
          plan.esPrueba
            ? "PRUEBA"
            : plan.esCortesia
              ? "CORTESIA"
              : "COMERCIAL"
        );
        setEstadoPlan(plan.estadoPlan !== false);
        setOrdenVisualizacion(String(plan.ordenVisualizacion ?? 0));
      } catch (error) {
        console.error("Error cargando plan:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire(
          "Error",
          error.response?.data?.message ||
            error.message ||
            "No se pudo cargar el plan.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    cargarPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermisoFormulario) {
      Swal.fire(
        "Sin permisos",
        edit
          ? "No tiene permisos para actualizar planes."
          : "No tiene permisos para crear planes.",
        "warning"
      );
      return;
    }

    if (esInstructor) {
      Swal.fire(
        "Acceso restringido",
        "La administración de planes corresponde al administrador.",
        "warning"
      );
      return;
    }

    const diasAvisoVencimiento = parsearListaDias(diasAvisoTexto);

    if (!codigoPlan.trim() || !nombrePlan.trim()) {
      Swal.fire("Atención", "Código y nombre son obligatorios.", "warning");
      return;
    }

    if (Number(cantidadDuracion) <= 0) {
      Swal.fire(
        "Atención",
        "La duración del plan debe ser mayor que cero.",
        "warning"
      );
      return;
    }

    if (Number(valorPlan) < 0) {
      Swal.fire(
        "Atención",
        "El valor del plan no puede ser negativo.",
        "warning"
      );
      return;
    }

    const payload = {
      codigoPlan: normalizarMayuscula(codigoPlan),
      nombrePlan: nombrePlan.trim(),
      descripcionPlan: descripcionPlan.trim(),
      tipoDuracion,
      cantidadDuracion: Number(cantidadDuracion),
      valorPlan: Number(valorPlan || 0),
      moneda: normalizarMayuscula(moneda || "COP"),
      diasGracia: Number(diasGracia || 0),
      diasAvisoVencimiento,
      permiteRenovacion,
      esPrueba: tipoPlan === "PRUEBA",
      esCortesia: tipoPlan === "CORTESIA",
      estadoPlan,
      ordenVisualizacion: Number(ordenVisualizacion || 0),
    };

    try {
      setLoading(true);

      const { data } = edit
        ? await axios.put(`/api/planes/update/${id}`, payload, authConfig())
        : await axios.post("/api/planes/add", payload, authConfig());

      await Swal.fire({
        icon: "success",
        title:
          data?.message ||
          (edit ? "Plan actualizado correctamente" : "Plan creado correctamente"),
        showConfirmButton: false,
        timer: 1600,
      });

      navigate("/cobros/planes");
    } catch (error) {
      console.error("Error guardando plan:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: edit ? "Error al actualizar" : "Error al registrar",
        text:
          error.response?.data?.message ||
          error.message ||
          "No se pudo guardar el plan.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (esInstructor) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          La creación y edición de planes está disponible únicamente para
          administración.
        </div>
        <button className="btn btn-dark" onClick={() => navigate("/cobros/planes")}>
          Volver
        </button>
      </div>
    );
  }

  if (edit && !puedeActualizar) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para actualizar planes.
        </div>
        <button className="btn btn-dark" onClick={() => navigate("/cobros/planes")}>
          Volver
        </button>
      </div>
    );
  }

  if (!edit && !puedeCrear) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          No tiene permisos para registrar planes.
        </div>
        <button className="btn btn-dark" onClick={() => navigate("/cobros/planes")}>
          Volver
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 fw-semibold">
                {edit ? "Actualizar Plan" : "Registrar Plan"}
              </h4>
              <p className="mb-0 text-secondary small">
                Configura duración, valor y reglas generales del plan.
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>

              <button
                type="button"
                className={`btn ${
                  edit ? "btn-outline-danger" : "btn-outline-dark"
                }`}
                onClick={handleCancelOrClean}
                disabled={loading}
              >
                <i
                  className={`fa-solid ${
                    edit ? "fa-xmark" : "fa-eraser"
                  } me-2`}
                ></i>
                {edit ? "Cancelar" : "Limpiar"}
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={actions}>
          <div className="row g-3">
            <div className="col-12 col-xl-8">
              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
                <h6 className="mb-0 fw-semibold">Información del plan</h6>
                <hr className="my-3" />

                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Código *
                    </label>
                    <input
                      className="form-control"
                      value={codigoPlan}
                      onChange={(e) =>
                        setCodigoPlan(convertirAMayuscula(e.target.value))
                      }
                      style={{ textTransform: "uppercase" }}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-8">
                    <label className="form-label small text-secondary text-uppercase">
                      Nombre del plan *
                    </label>
                    <input
                      className="form-control"
                      value={nombrePlan}
                      onChange={(e) => setNombrePlan(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Descripción
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={descripcionPlan}
                      onChange={(e) => setDescripcionPlan(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
                <h6 className="mb-0 fw-semibold">Duración y valor</h6>
                <hr className="my-3" />

                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Tipo de duración *
                    </label>
                    <select
                      className="form-select"
                      value={tipoDuracion}
                      onChange={(e) => setTipoDuracion(e.target.value)}
                      required
                    >
                      <option value="DIAS">Días</option>
                      <option value="MESES">Meses</option>
                      <option value="ANIOS">Años</option>
                      <option value="PERSONALIZADO">Personalizado</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Cantidad *
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      value={cantidadDuracion}
                      onChange={(e) => setCantidadDuracion(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Valor *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="form-control"
                      value={valorPlan}
                      onChange={(e) => setValorPlan(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Moneda *
                    </label>
                    <input
                      className="form-control"
                      value={moneda}
                      onChange={(e) =>
                        setMoneda(convertirAMayuscula(e.target.value))
                      }
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Días de gracia
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={diasGracia}
                      onChange={(e) => setDiasGracia(e.target.value)}
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label small text-secondary text-uppercase">
                      Orden visualización
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      value={ordenVisualizacion}
                      onChange={(e) => setOrdenVisualizacion(e.target.value)}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label small text-secondary text-uppercase">
                      Avisos de vencimiento
                    </label>
                    <input
                      className="form-control"
                      value={diasAvisoTexto}
                      onChange={(e) => setDiasAvisoTexto(e.target.value)}
                      placeholder="7, 3, 1"
                    />
                    <div className="form-text">
                      Escriba los días separados por coma. Ejemplo: 7, 3, 1.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-4">
              <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
                <h6 className="mb-0 fw-semibold">Comportamiento</h6>
                <hr className="my-3" />

                <div className="mb-3">
                  <label className="form-label small text-secondary text-uppercase">
                    Tipo de plan
                  </label>
                  <select
                    className="form-select"
                    value={tipoPlan}
                    onChange={(e) => setTipoPlan(e.target.value)}
                  >
                    <option value="COMERCIAL">Comercial</option>
                    <option value="PRUEBA">Prueba</option>
                    <option value="CORTESIA">Cortesía</option>
                  </select>
                </div>

                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={permiteRenovacion}
                    onChange={(e) => setPermiteRenovacion(e.target.checked)}
                    id="permiteRenovacion"
                  />
                  <label className="form-check-label" htmlFor="permiteRenovacion">
                    Permite renovación
                  </label>
                </div>

                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={estadoPlan}
                    onChange={(e) => setEstadoPlan(e.target.checked)}
                    id="estadoPlan"
                  />
                  <label className="form-check-label" htmlFor="estadoPlan">
                    Plan activo
                  </label>
                </div>

                <div className="alert alert-light border mt-4 mb-0 small text-secondary">
                  Los planes inactivos se conservan para proteger el historial,
                  pero no deben usarse en nuevas suscripciones.
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <button
                  type="submit"
                  className="btn btn-dark w-100 fw-semibold py-2"
                  disabled={loading || !tienePermisoFormulario}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                    } me-2`}
                  ></i>
                  {loading
                    ? "Guardando…"
                    : edit
                      ? "Guardar cambios"
                      : "Registrar plan"}
                </button>
              </div>

              <div className="col-12 col-md-6">
                <button
                  type="button"
                  className={`btn w-100 fw-semibold py-2 ${
                    edit ? "btn-outline-danger" : "btn-outline-secondary"
                  }`}
                  onClick={handleCancelOrClean}
                  disabled={loading}
                >
                  {edit ? "Cancelar" : "Limpiar"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrarPlanes;
